import React, {
  createContext,
  useContext,
  useId,
  useRef,
  useMemo,
  useState,
  useCallback,
  useLayoutEffect,
} from 'react';
import './Sidenote.css';

/*
 * Tufte-style notes.
 *
 * Wrap a page (or any section) in <TufteContent> and write notes inline with
 * <Sidenote> / <Marginnote>:
 *
 *   <TufteContent className="project-content">
 *     <p>
 *       Some claim<Sidenote>The supporting detail.</Sidenote> that needs a note.
 *     </p>
 *   </TufteContent>
 *
 * On wide screens the note sits in the right margin next to its reference. On
 * small screens the margin disappears, the reference becomes a tappable link,
 * and every note is collected into a numbered footnotes list at the bottom of
 * the TufteContent block.
 *
 * Hovering the reference, the margin note, or the footnote highlights the
 * matching number/marker everywhere it appears.
 */

const TufteContext = createContext(null);

// useId() returns ids containing ':' which are not valid in URL fragments or
// querySelector — strip them for use in DOM ids / hrefs.
const safeId = (id) => `note-${id.replace(/[^a-zA-Z0-9_-]/g, '')}`;

// Smooth-scroll to a same-page target instead of the default instant jump.
const jumpTo = (e, targetId) => {
  const el = document.getElementById(targetId);
  if (!el) return;
  e.preventDefault();
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

export const TufteContent = ({ children, className = '', as: Tag = 'div' }) => {
  // Live registry of every note: id -> { node, getContent, type }. Kept in a
  // ref (not state) so registering doesn't churn identity; an ordered list of
  // ids is mirrored into state to drive rendering.
  const entriesRef = useRef(new Map());
  const [orderedIds, setOrderedIds] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);

  const recompute = useCallback(() => {
    const entries = [...entriesRef.current.values()];
    // Sort by actual document position so numbering matches reading order,
    // regardless of mount timing.
    entries.sort((a, b) => {
      const na = a.node;
      const nb = b.node;
      if (!na || !nb || na === nb) return 0;
      const pos = na.compareDocumentPosition(nb);
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });
    setOrderedIds(entries.map((e) => e.id));
  }, []);

  const register = useCallback(
    (id, node, getContent, type) => {
      entriesRef.current.set(id, { id, node, getContent, type });
      recompute();
    },
    [recompute]
  );

  const unregister = useCallback(
    (id) => {
      entriesRef.current.delete(id);
      recompute();
    },
    [recompute]
  );

  // Assign a sequential number to numbered notes (sidenotes); margin notes get
  // a ⊕ marker instead.
  const markers = useMemo(() => {
    const map = {};
    let n = 0;
    orderedIds.forEach((id) => {
      const entry = entriesRef.current.get(id);
      if (!entry) return;
      map[id] = entry.type === 'margin' ? '⊕' : String(++n);
    });
    return map;
  }, [orderedIds]);

  const ctx = useMemo(
    () => ({ register, unregister, markers, hoveredId, setHoveredId }),
    [register, unregister, markers, hoveredId]
  );

  return (
    <TufteContext.Provider value={ctx}>
      <Tag className={`tufte-content ${className}`.trim()}>
        {children}
        {orderedIds.length > 0 && (
          <ol className="footnotes" aria-label="Footnotes">
            {orderedIds.map((id) => {
              const entry = entriesRef.current.get(id);
              const active = hoveredId === id;
              return (
                <li
                  key={id}
                  id={`fn-${id}`}
                  className={`footnote${active ? ' note-active' : ''}`}
                  onMouseEnter={() => setHoveredId(id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={(e) => jumpTo(e, `fnref-${id}`)}
                >
                  <a className="footnote-backref" href={`#fnref-${id}`}>
                    {markers[id]}
                  </a>{' '}
                  {entry?.getContent()}
                </li>
              );
            })}
          </ol>
        )}
      </Tag>
    </TufteContext.Provider>
  );
};

const useNote = (children, type) => {
  const ctx = useContext(TufteContext);
  const rawId = useId();
  const id = safeId(rawId);
  const markerRef = useRef(null);
  // Keep the latest children available to the footnote renderer without
  // re-registering on every render.
  const contentRef = useRef(children);
  contentRef.current = children;

  useLayoutEffect(() => {
    if (!ctx) return undefined;
    ctx.register(id, markerRef.current, () => contentRef.current, type);
    return () => ctx.unregister(id);
    // register/unregister are stable; id/type never change for a given note.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const marker = ctx ? ctx.markers[id] : undefined;
  const active = ctx ? ctx.hoveredId === id : false;
  const hoverProps = ctx
    ? {
        onMouseEnter: () => ctx.setHoveredId(id),
        onMouseLeave: () => ctx.setHoveredId(null),
      }
    : {};
  return { id, marker, markerRef, active, hoverProps };
};

export const Sidenote = ({ children }) => {
  const { id, marker, markerRef, active, hoverProps } = useNote(children, 'side');
  const activeClass = active ? ' note-active' : '';
  return (
    <>
      <a
        ref={markerRef}
        id={`fnref-${id}`}
        href={`#fn-${id}`}
        className={`sidenote-number${activeClass}`}
        onClick={(e) => jumpTo(e, `fn-${id}`)}
        {...hoverProps}
      >
        <sup>{marker}</sup>
      </a>
      <span
        className={`sidenote${activeClass}`}
        onClick={(e) => jumpTo(e, `fnref-${id}`)}
        {...hoverProps}
      >
        <sup className="sidenote-marker">{marker}</sup> {children}
      </span>
    </>
  );
};

export const Marginnote = ({ children, symbol = '⊕' }) => {
  const { id, markerRef, active, hoverProps } = useNote(children, 'margin');
  const activeClass = active ? ' note-active' : '';
  return (
    <>
      <a
        ref={markerRef}
        id={`fnref-${id}`}
        href={`#fn-${id}`}
        className={`margin-symbol${activeClass}`}
        onClick={(e) => jumpTo(e, `fn-${id}`)}
        {...hoverProps}
      >
        {symbol}
      </a>
      <span
        className={`marginnote${activeClass}`}
        onClick={(e) => jumpTo(e, `fnref-${id}`)}
        {...hoverProps}
      >
        {children}
      </span>
    </>
  );
};

export default Sidenote;
